/**
 * Shared, framework-free helpers for the Breakdown/review-feedback tab (#342).
 * Kept out of BreakdownTab.js/ItemBlock.js to avoid a circular import between
 * the two (both need approval-state + filter logic).
 */

// F-20 / REQ-6106 (D-01): the display-state precedence policy, client-side. Multi-
// reviewer rollup rule for ONE item's approve pill (D-01, spec Edge case 5),
// mirrored from the core layer's `approval_display_state` (core/requirements/
// feedback.py) — except we use the REST-precomputed `stale` flag per approval
// instead of recomputing the content hash client-side (spec: "the REST layer
// precomputes stale per approval so you don't need to hash anything client-side").
//
// The precedence below MUST match `approval_display_state` case for case. It did not:
// this function predates #603, and two of its rules were wrong against the merged
// backend contract.
//
// F-09 / REQ-6099 (#603, D-08) — `approved_over_objection` was missing entirely. A
// fresh approval carrying a justification, while a rejection is on record, outranks
// that rejection: the veto is soft. Without this case the pill rendered a flat
// `not_approved`, i.e. an override the backend had ACCEPTED was invisible on screen.
//
// F-11 / REQ-6101 (#603) — staleness is SYMMETRIC now. The old rule vetoed on ANY
// `not_approved` regardless of `stale`, so a rejection whose objected-to text had
// since been rewritten — including rewritten to fix the very thing objected to —
// kept the item red for ever, with the REST payload reporting `stale: true` and the
// UI ignoring it. A stale rejection stays visible but no longer blocks.
export function deriveApprovalState(approvals) {
  if (!approvals || approvals.length === 0) return 'pending';
  const list = approvals.filter(Boolean);
  const freshApproved = list.filter((a) => a.state === 'approved' && !a.stale);
  // 1. A justified fresh approval over a rejection on record — both decisions kept.
  if (freshApproved.some((a) => a.justification)
      && list.some((a) => a.state === 'not_approved')) return 'approved_over_objection';
  // 2. Any FRESH rejection vetoes.
  if (list.some((a) => a.state === 'not_approved' && !a.stale)) return 'not_approved';
  // 3. Any fresh approval.
  if (freshApproved.length > 0) return 'approved';
  // 4. Anything recorded but stale — an approval OR a rejection.
  if (list.some((a) => (a.state === 'approved' || a.state === 'not_approved') && a.stale)) return 'stale';
  return 'pending';
}

// F-09 / REQ-6099 (#603): mirrors the core layer's `_ROLLUP_BUCKET`
// (core/requirements/feedback.py). The aggregate COUNTS keep their four existing keys —
// "approved over objection" counts as approved; the distinction lives on the per-item
// chip, not in the tallies. Without this the bare `counts[state] += 1` below evaluated
// `undefined + 1` and every rollup carrying one contested approval rendered `NaN`
// (the progress chips and the F-15 descendant chip both read these counts).
const ROLLUP_BUCKET = { approved_over_objection: 'approved' };

// F-09 / REQ-6099: the states that mean "this item carries a live sign-off". Used by the
// Breakdown 'unapproved' filter, which otherwise listed an approved-over-objection item
// as unapproved — the override IS an approval, the objection is retained beside it.
export const APPROVED_STATES = new Set(['approved', 'approved_over_objection']);

// F-11 / REQ-6101 (#603): which decision the `stale` rollup refers to, derived from the
// raw rows the Breakdown payload already carries. Mirrors `stale_decision_kind` in
// core/requirements/feedback.py, and exists for the same reason: staleness is symmetric
// now, so a surface that hard-codes "Approved · stale" mislabels a lapsed REJECTION as
// an approval. Returns 'approved' | 'not_approved' | 'mixed' | null (nothing stale).
export function staleDecisionKind(approvals) {
  const kinds = new Set(
    (approvals || []).filter(
      (a) => a && a.stale && (a.state === 'approved' || a.state === 'not_approved'),
    ).map((a) => a.state),
  );
  if (kinds.size === 0) return null;
  if (kinds.size === 1) return [...kinds][0];
  return 'mixed';
}

// item_uuid -> count of OPEN comments (top-level + replies both count, same as
// the design mockup's simple `comments.filter(open).length`).
export function computeOpenCounts(threads) {
  const counts = {};
  Object.entries(threads || {}).forEach(([id, list]) => {
    counts[id] = (list || []).filter((c) => c && c.status === 'open').length;
  });
  return counts;
}

function itemMatchesFilter(node, filter, approvalsByItem, openCountByItem) {
  if (filter === 'all') return true;
  const state = deriveApprovalState((approvalsByItem || {})[node.id] || []);
  const openCount = (openCountByItem || {})[node.id] || 0;
  if (filter === 'open') return openCount > 0;
  // F-09 / REQ-6099 (#603, D-08): an approval that superseded an objection is still an
  // approval — it must not be listed as outstanding work by the 'unapproved' filter.
  if (filter === 'unapproved') return !APPROVED_STATES.has(state);
  if (filter === 'stale') return state === 'stale';
  return true;
}

/**
 * Walk the subtree (an array of top-level nodes) and return the Set of node
 * ids that stay visible under `filter` — a node is visible if IT matches, or
 * ANY descendant matches (so ancestors of a match are never hidden — spec:
 * "parents of matches stay visible for context"). Filtering never refetches;
 * this runs client-side over the already-fetched payload.
 */
export function collectVisibleIds(nodes, filter, approvalsByItem, openCountByItem) {
  const out = new Set();
  function walk(node) {
    const selfMatch = itemMatchesFilter(node, filter, approvalsByItem, openCountByItem);
    let anyChildVisible = false;
    (node.children || []).forEach((child) => {
      if (walk(child)) anyChildVisible = true;
    });
    const visible = selfMatch || anyChildVisible;
    if (visible) out.add(node.id);
    return visible;
  }
  (nodes || []).forEach(walk);
  return out;
}

/**
 * Client-side mirror of the core layer's `compute_rollup` (core/requirements/
 * feedback.py) — walks the WHOLE subtree (root container included, same as
 * core) rolling up approved/stale/not_approved/pending + total open comments.
 *
 * BreakdownTab recomputes this locally after every approve/comment mutation
 * (spec Edge case 12: "progress chips derive from the review payload;
 * invalidate on comment/approval mutations") rather than trusting a
 * one-shot server value that would otherwise go stale after the first local
 * action without a full refetch.
 */
export function computeRollup(rootNode, approvalsByItem, threads) {
  const counts = { approved: 0, stale: 0, not_approved: 0, pending: 0, open_comments: 0 };
  if (!rootNode) return counts;
  const openCounts = computeOpenCounts(threads);
  function walk(node) {
    const state = deriveApprovalState((approvalsByItem || {})[node.id] || []);
    // F-09 / REQ-6099: bucket into the four existing keys — see ROLLUP_BUCKET above.
    counts[ROLLUP_BUCKET[state] || state] += 1;
    counts.open_comments += openCounts[node.id] || 0;
    (node.children || []).forEach(walk);
  }
  walk(rootNode);
  return counts;
}

// F-15 / REQ-6105 (#599, D-02): a container's OWN approval is independent of its
// descendants (F-14) — so a container needs a SEPARATE indicator for "how is the
// subtree beneath me doing", distinct from its own approve pill. This walks a
// container's CHILDREN only (the node itself is excluded — its own state is
// already shown by its ApprovePill) using the same per-item rollup rule as
// `computeRollup`, over the SAME `approvals` map ItemBlock already has loaded for
// the whole subtree — no extra fetch.
export function computeDescendantsRollup(node, approvalsByItem) {
  const counts = { approved: 0, stale: 0, not_approved: 0, pending: 0, total: 0 };
  if (!node) return counts;
  function walk(n) {
    const state = deriveApprovalState((approvalsByItem || {})[n.id] || []);
    // F-09 / REQ-6099: same four-key bucketing as computeRollup — see ROLLUP_BUCKET.
    counts[ROLLUP_BUCKET[state] || state] += 1;
    counts.total += 1;
    (n.children || []).forEach(walk);
  }
  (node.children || []).forEach(walk);
  return counts;
}

export default {
  deriveApprovalState, computeOpenCounts, collectVisibleIds, computeRollup,
  computeDescendantsRollup, staleDecisionKind, APPROVED_STATES,
};
